/**
 * The duel overlay: the one moment the game stops and asks the player a question.
 *
 * Everything about it is shaped by the fact that both players are *frozen* while it is up. That
 * makes it the only piece of UI in the game allowed to take the whole bottom of the screen and
 * the only one allowed to swallow pointer events — there is nothing to steer, so there is no
 * dead zone to create. It is also why the buttons are sized for a thumb rather than a cursor:
 * mobile is a target, the window is DUEL_DECIDE_TICKS long, and a player hunting for a small
 * button is a player who gets a random answer picked for them.
 *
 * The bar is a CSS animation against a deadline on the client's clock, not a re-render:
 * `DuelView` changes only when the duel actually changes state, so React renders this a handful
 * of times per duel while the bar runs at whatever rate the compositor likes. The seconds beside
 * it are the one exception, and a deliberate one — see `CountdownSeconds`.
 *
 * The buttons come from `DuelView.choices`, which comes from the mini-game registry keyed by the
 * duel's `gameId` — and so does their artwork, which is `MiniGameChoiceArt` on the choice rather
 * than a drawing in this file. There is no rock-paper-scissors here, and adding a second
 * mini-game needs no change to it. The renderer for those paths is `./MiniGameChoiceIcon.tsx`,
 * its own module since the instructions modal started mounting the same icons — `DuelPotBar`'s
 * extraction one file along, and for its reason.
 *
 * ## The pot
 *
 * The bar itself is `./DuelPotBar.tsx` — its own component since it gained a second mount, so an
 * instructions page can show the same picture against a made-up percentage rather than drawing a
 * second one that drifts. What is decided here is only *whether* there is one.
 *
 * A mini-game may play for a POT: a stake that climbs on the duel's own clock and bursts at a
 * point neither player knows. Whether this duel has one is `DuelView.pot`, which is the
 * *registry's* answer read through `miniGamePotOf` where `getMiniGame` is already being called
 * — so this file asks "does the running game have a pot" and never "is this chicken", which is
 * the same line the simulation draws with `projectileSpecOf` in the power-up registry. Rock-
 * paper-scissors declares no pot, so every line below that reads one is inert for it and its
 * overlay is byte for byte what it always was.
 *
 * The climb is the server's, on every frame (`DuelSnapshot.pot`), and there is no clock on this
 * side: `inventory-strip.ts`' rule, one panel along. What is *not* on the wire, and must never
 * be, is the tick the pot bursts on — see `../hud/duel-pot.ts`.
 *
 * ## The secrecy rule
 *
 * Nothing on screen may attribute a choice to the opponent before both sides are locked. The
 * server is the enforcement — `toDuelSnapshot` withholds the other choice until the reveal, so
 * `duel.opponentChoice` is simply null before then — and this file's half of it is that the
 * reveal panel is drawn on `phase === 'revealed'` and nothing else ever reads that field.
 */

import { useEffect, useRef, useState } from 'react';
import type { MiniGameChoice } from '@chompdome/shared/minigames';
import type { DuelView } from '../net/connection';
import { pickDuelLine } from './duel-lines';
import { type DuelPotReadout, readDuelPot } from './duel-pot';
import { DuelPotBar } from './DuelPotBar';
import { MiniGameChoiceIcon } from './MiniGameChoiceIcon';
import { useHudSettings } from './HudSettings';
import { useTapActivate } from './tap-activate';

export interface DuelOverlayProps {
  duel: DuelView;
  /** Send an answer. Ignored by the connection once our side is locked. */
  onChoose: (choice: string) => void;
  /** Injected so the countdown is deterministic under test. */
  now?: () => number;
}

const OUTCOME_TEXT: Record<'win' | 'loss' | 'draw', string> = {
  win: 'You win!',
  loss: 'You lose',
  draw: 'Draw',
};

const OUTCOME_TONE: Record<'win' | 'loss' | 'draw', string> = {
  win: 'text-primary',
  loss: 'text-destructive',
  draw: 'text-muted-foreground',
};

const OUTCOME_BLURB: Record<Outcome, string> = {
  win: 'You take a bite out of them.',
  loss: 'They take a bite out of you — run.',
  draw: 'Nobody wins. Break away.',
};

type Outcome = 'win' | 'loss' | 'draw';

/**
 * The same three outcomes, for a duel that was playing for a pot.
 *
 * **A DRAW IS A BUST**, which is the one thing a pot game says that a plain duel cannot: nobody
 * held it when it went, so it is not "nobody wins" — it is on the floor, both players paid for
 * it, and the room is already racing for it. See `spawnPrizeShards`.
 */
const POT_OUTCOME_TEXT: Record<Outcome, string> = {
  win: 'You took it!',
  loss: 'Too slow',
  draw: 'BURST',
};

const POT_OUTCOME_BLURB: Record<Outcome, string> = {
  win: 'The pot is yours. Go and spend it.',
  loss: 'They got their hand in first.',
  draw: 'It burst. Nobody holds it — it is on the floor, and so is everybody else.',
};

/** What the two lock pips say. A pot game is holding rather than choosing, and it shows. */
const PIP_WORDS = { locked: 'ready', open: 'choosing' } as const;
const POT_PIP_WORDS = { locked: 'took it', open: 'holding' } as const;

/**
 * The one line under the header: what is happening and why nobody is moving.
 *
 * A function rather than three nested ternaries in the markup, because a pot changes what every
 * branch of it says: "pick one" is wrong for a game whose whole question is *when*, and "evenly
 * matched" is wrong for a game a client cannot even draw. It reads `pot` — the registry's
 * answer — and never a game id.
 */
function statusLine(duel: DuelView, pot: DuelPotReadout | null, outcome: Outcome): string {
  if (duel.phase === 'revealed') {
    return pot === null ? OUTCOME_BLURB[outcome] : POT_OUTCOME_BLURB[outcome];
  }
  if (pot !== null) {
    if (duel.youLocked) return 'Your hand is on it. Nothing left to decide.';
    if (duel.opponentLocked) return `${duel.opponentName} went for it.`;
    return 'It climbs while you watch it — and it bursts, and nobody knows when.';
  }
  if (duel.youLocked) {
    return duel.opponentLocked
      ? 'Both locked in. Hands out…'
      : `Locked in — waiting for ${duel.opponentName}…`;
  }
  // No buttons and no pot is a mini-game this build has never heard of: a stale install against a
  // server that has moved on. The freeze is real and the room will settle it without us, so the
  // honest thing is to say so rather than to tell somebody to pick from nothing.
  if (duel.choices.length === 0) {
    return 'This game arrived with an update you do not have yet. Sit tight — it settles itself.';
  }
  return duel.stake === null
    ? 'Evenly matched — nobody can eat anybody. Pick one; you are both frozen until you do.'
    : 'A duel gun put you both here — size does not count. Pick one; you are both frozen until you do.';
}

export function DuelOverlay({ duel, onChoose, now = Date.now }: DuelOverlayProps) {
  return (
    <div
      role="dialog"
      aria-label="Mini-game duel"
      data-testid="duel-overlay"
      /*
       * The bottom padding clears the on-screen action controls (`ActionButtons`: 5rem tall at
       * `bottom-4`, and still drawn during a duel), so the card and the split button are never
       * stacked on top of each other on a portrait phone. It reverts at `md`, which is past
       * every width `useTouchControls` draws those buttons at for a fine pointer. The safe-area
       * inset is *added* to it rather than maxed against it, because the controls are themselves
       * already inside it (`.hud-safe-inset`).
       */
      className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-col items-center gap-3 p-3 pb-[calc(7rem+var(--chomp-safe-bottom))] sm:gap-4 sm:p-6 md:pb-[max(1.5rem,var(--chomp-safe-bottom))]"
    >
      <DuelCard duel={duel} onChoose={onChoose} now={now} />
    </div>
  );
}

/**
 * THE CARD ITSELF, WITHOUT THE ARENA AROUND IT — and the extraction is the point rather than
 * tidiness, which is `DuelPotBar`'s and `MiniGameChoiceIcon`'s note one file along and for their
 * exact reason: it gained a second mount site.
 *
 * What stayed in `DuelOverlay` is everything that is about being drawn over a live arena and
 * nothing else: the `role="dialog"` that `keyboardActs` reads to disarm SPACE / W / Q (which a
 * duellist can afford, being frozen), the `pointer-events-none` layer, and the bottom padding
 * that clears the on-screen action controls. **None of that is true on an instructions page**,
 * where a nested dialog role would be an error and an absolutely-positioned arena overlay would
 * have nothing to position against.
 *
 * What is here is a card that takes a `DuelView`, an `onChoose` and a clock — no connection, no
 * wire, no scene — which is the same property that let `DuelPotBar` and `StarChoiceCard` be
 * mounted in the how-to-play modal. A second drawing of the duel card would be wrong the first
 * time a rung, a word or a pip moved; this cannot drift from itself.
 */
export function DuelCard({ duel, onChoose, now = Date.now }: DuelOverlayProps) {
  const remainingMs = Math.max(0, duel.endsAtMs - now());
  const revealed = duel.phase === 'revealed';
  const outcome = duel.outcome ?? 'draw';
  // `youLocked` is the server's word and `yourChoice` is our own tap: the connection will still
  // send a different answer right up until the lock lands, so anything that said "locked in" off
  // the local pick would be saying it about a choice that can still change. The pressed button is
  // the immediate feedback; `youLocked` is the fact. Both are read where they are used.
  //
  // The registry's answer, not a branch on a game id. Null for rock-paper-scissors, and null for
  // a game this build does not have — both draw the overlay that has always been here.
  const pot = readDuelPot(duel.pot);
  // Battery saver may settle the pot's movement and may never take the number or the button
  // away: it changes fidelity, never visibility (CLAUDE.md, "Battery on a phone").
  const { batterySaver } = useHudSettings();

  return (
    <div
      data-testid="duel-card"
      className="pointer-events-auto w-full max-w-md rounded-2xl border border-border bg-card/95 p-4 shadow-2xl backdrop-blur"
    >
      <header className="flex items-baseline justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Duel
        </div>
        <div className="truncate text-sm font-bold">vs {duel.opponentName}</div>
      </header>

      {/* The freeze is the thing a player most needs explained, so it is said outright. */}
      <p role="status" className="mt-1 text-xs text-muted-foreground">
        {statusLine(duel, pot, outcome)}
      </p>

      {/*
       * WHAT AM I PLAYING FOR, and it is drawn only when there is an answer.
       *
       * A contact duel stakes a share of whoever loses, so there is no number to print until it
       * is over; a gun duel fixed one at the tick it began and both players are looking at this
       * card for ten seconds. It is the server's own figure off `DuelSnapshot.stake` — never two
       * masses multiplied on this side — and it is rounded for reading rather than for
       * arithmetic, which is the treatment every other mass in the HUD gets.
       */}
      {duel.stake === null ? null : (
        <p data-testid="duel-stake" className="mt-1 text-xs font-semibold text-primary">
          Playing for {Math.round(duel.stake).toLocaleString()} mass
        </p>
      )}

      {/*
       * THE POT, and it is drawn in both phases on purpose: it runs live while the duel is
       * collecting and the server freezes it at the settling tick, so the number under the
       * verdict is the number the payout was measured at rather than whatever the climb had
       * reached by the time the reveal was rendered.
       */}
      {pot === null ? null : <DuelPotBar pot={pot} settled={revealed} still={batterySaver} />}

      {/*
       * THE COUNTDOWN IS DRAWN ONLY FOR A GAME WITH NO POT, BECAUSE A POT *IS* THE COUNTDOWN.
       *
       * `CHICKEN_TICKS_PER_PERCENT` is `DUEL_DECIDE_TICKS / 100` — the pot's constants card says
       * so out loud, and says why there is deliberately no `CHICKEN_*_DURATION` beside it: a
       * second number for the same ten seconds is a second number to forget to change. So the
       * climb reaching 100% *is* the answering window closing, and drawing a countdown beside the
       * bar was the same clock twice, in two units, one of which also says what the time is worth.
       * The player reported it as exactly that: "why does chicken have a countdown bar? It should
       * just be in the inside thin bar progressing in the real bar next to percentages."
       *
       * Of the two, the bar is strictly the better one — it carries the rungs, the risk bands and
       * the multiplier, none of which a countdown can say — so the countdown is what goes. Which
       * one is drawn is the registry's answer (`pot`), never a branch on a game id, so a second
       * push-your-luck game gets this for free and rock-paper-scissors keeps the countdown it has
       * always had, being a game where nothing else on the card says how long is left.
       */}
      {pot === null ? (
        <div className="mt-3 flex items-center gap-2">
          <Countdown key={`${duel.id}:${duel.phase}`} remainingMs={remainingMs} />
          <CountdownSeconds endsAtMs={duel.endsAtMs} now={now} />
        </div>
      ) : null}

      {revealed ? (
        pot === null ? (
          <Reveal duel={duel} outcome={outcome} />
        ) : (
          <PotReveal outcome={outcome} />
        )
      ) : (
        <>
          {duel.choices.length === 0 ? null : (
            <div
              className="mt-3 grid gap-2"
              style={{ gridTemplateColumns: `repeat(${duel.choices.length}, 1fr)` }}
            >
              {duel.choices.map((choice) => (
                <ChoiceButton
                  key={choice.id}
                  choice={choice}
                  picked={duel.yourChoice?.id === choice.id}
                  locked={duel.youLocked}
                  /*
                   * SENT, NEVER DONE — `sayingSent` and `slotSent`'s arrangement, said on the
                   * control rather than beside it. `yourChoice` is our own tap and `youLocked`
                   * is the server's word, so the two together are exactly "gone" against
                   * "landed", and the server may still refuse it silently.
                   *
                   * Only for a pot game: a rock-paper-scissors button is one of three and its
                   * pressed state already says everything, where a single-button game has
                   * nothing else on screen to say the press was heard.
                   */
                  note={
                    pot === null || duel.yourChoice?.id !== choice.id
                      ? null
                      : duel.youLocked
                        ? 'In'
                        : 'Sent…'
                  }
                  onChoose={onChoose}
                />
              ))}
            </div>
          )}

          {/*
           * One silly line per duel, seeded off the duel's id so it is the same on every render
           * — see `duel-lines.ts`. It is deliberately below the buttons: the thing a frozen
           * player is looking for is the control, and the joke is what they read once they have
           * found it.
           */}
          <p
            data-testid="duel-line"
            className="mt-3 text-center text-[11px] italic leading-snug text-muted-foreground"
          >
            {pickDuelLine(duel.id)}
          </p>
        </>
      )}

      <footer className="mt-3 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider">
        <LockPip
          label="You"
          locked={duel.youLocked}
          words={pot === null ? PIP_WORDS : POT_PIP_WORDS}
        />
        {/*
         * What the opponent picked is not on the wire yet — only *that* they picked. For a pot
         * game that is the whole feature rather than a consolation: a grab is public the instant
         * it happens (`secretUntilLocked: false`), so this pip is where the other player's nerve
         * is read, and it needs no field of its own to say so.
         */}
        <LockPip
          label={duel.opponentName}
          locked={duel.opponentLocked}
          words={pot === null ? PIP_WORDS : POT_PIP_WORDS}
        />
      </footer>
    </div>
  );
}

/**
 * The phase timer.
 *
 * Keyed on the duel and its phase by the caller, so a new phase remounts it and the animation
 * restarts from full rather than resuming wherever the last one left off.
 *
 * The duration is captured on mount and never recomputed. Within a phase the overlay still
 * re-renders — locking a choice in changes it — and writing a fresh `animation` value on one of
 * those renders would restart the bar from full, which reads as the game handing the player back
 * time they no longer have. The remount is the *only* thing allowed to restart it.
 */
function Countdown({ remainingMs }: { remainingMs: number }) {
  const [durationMs] = useState(() => Math.max(remainingMs, 1));

  return (
    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-border" aria-hidden>
      <div
        className="h-full origin-left bg-primary"
        style={{ animation: `chompdome-duel-countdown ${durationMs}ms linear forwards` }}
      />
    </div>
  );
}

/**
 * The seconds left, as a number that actually counts down.
 *
 * This is the one place in the HUD that re-renders because time passed, and it is bounded to
 * exactly that: it wakes on each whole-second boundary of its own deadline — ten times over a
 * whole duel, not twenty times a second — and it is a leaf, so what re-renders is one span and
 * nothing above it. The bar beside it is still pure CSS and still does the smooth part.
 *
 * A bar alone was the thing the player report was about: it says "time is passing" and never
 * says how much is left, which is the number somebody deciding under pressure wants. `now` is
 * read through a ref so the schedule depends on the deadline alone — the prop is a fresh arrow
 * on most renders, and a schedule that restarted on each of those would drift.
 */
function CountdownSeconds({ endsAtMs, now }: { endsAtMs: number; now: () => number }) {
  const nowRef = useRef(now);
  nowRef.current = now;
  const [remainingMs, setRemainingMs] = useState(() => Math.max(0, endsAtMs - now()));

  useEffect(() => {
    let handle: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      const left = Math.max(0, endsAtMs - nowRef.current());
      setRemainingMs(left);
      if (left <= 0) return;
      // The next boundary, not a fixed interval: the displayed number is a `ceil`, so it changes
      // when the remainder runs out and at no other moment.
      handle = setTimeout(schedule, left % 1000 === 0 ? 1000 : left % 1000);
    };

    schedule();
    return () => clearTimeout(handle);
  }, [endsAtMs]);

  const seconds = Math.ceil(remainingMs / 1000);
  return (
    <span
      role="timer"
      aria-live="off"
      aria-label={`${seconds} seconds left`}
      data-testid="duel-seconds"
      className={`w-7 shrink-0 text-right text-sm font-black tabular-nums ${
        seconds <= 3 ? 'text-destructive' : 'text-muted-foreground'
      }`}
    >
      {seconds}
    </span>
  );
}

/**
 * Both picks, side by side, and who that made the winner.
 *
 * Only ever rendered at `phase === 'revealed'`, which is the whole of the client's half of the
 * secrecy rule: `duel.opponentChoice` is null until the server sends it, and this is the one
 * place in the file that reads it.
 */
function Reveal({ duel, outcome }: { duel: DuelView; outcome: 'win' | 'loss' | 'draw' }) {
  return (
    <div className="chompdome-duel-reveal mt-3" data-testid="duel-reveal">
      <div className="flex items-center justify-center gap-3 sm:gap-4">
        <Pick
          choice={duel.yourChoice}
          caption="You"
          won={outcome === 'win'}
          beaten={outcome === 'loss'}
        />
        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          vs
        </span>
        <Pick
          choice={duel.opponentChoice}
          caption="Them"
          won={outcome === 'loss'}
          beaten={outcome === 'win'}
        />
      </div>
      <div className={`mt-2 text-center text-lg font-black ${OUTCOME_TONE[outcome]}`}>
        {OUTCOME_TEXT[outcome]}
      </div>
    </div>
  );
}

/**
 * One side's pick at the reveal.
 *
 * The winner is ringed and the loser is faded, so the verdict is readable from the picture
 * before the word under it is read at all — which is most of what a flourish is for on a screen
 * somebody is watching for less than a second (DUEL_REVEAL_TICKS).
 */
function Pick({
  choice,
  caption,
  won,
  beaten,
}: {
  choice: MiniGameChoice | null;
  caption: string;
  won: boolean;
  beaten: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span
        className={`flex h-14 w-14 items-center justify-center rounded-full border-2 transition-colors ${
          won
            ? 'border-primary bg-primary/15 text-primary'
            : beaten
              ? 'border-border bg-background/40 text-muted-foreground opacity-60'
              : 'border-border bg-background/60 text-foreground'
        }`}
      >
        {choice === null ? (
          <span aria-hidden className="text-2xl leading-none">
            ❔
          </span>
        ) : (
          <MiniGameChoiceIcon choice={choice} className="h-8 w-8" />
        )}
      </span>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {caption}
      </span>
      <span className="sr-only">
        {caption} chose {choice?.label ?? 'nothing'}
      </span>
    </div>
  );
}

function LockPip({
  label,
  locked,
  words,
}: {
  label: string;
  locked: boolean;
  words: { readonly locked: string; readonly open: string };
}) {
  return (
    <span className={locked ? 'text-primary' : 'text-muted-foreground'}>
      <span aria-hidden>{locked ? '●' : '○'}</span> <span className="truncate">{label}</span>{' '}
      {locked ? words.locked : words.open}
    </span>
  );
}

/**
 * The verdict for a duel that was playing for a pot.
 *
 * There are no hands to show side by side — both players answered the same thing and the whole
 * difference is *when* — so what the reveal draws is the word and the reason, over the settled
 * dial the caller has already put above it.
 */
function PotReveal({ outcome }: { outcome: Outcome }) {
  return (
    <div className="chompdome-duel-reveal mt-3 text-center" data-testid="duel-pot-reveal">
      <div className={`text-lg font-black ${OUTCOME_TONE[outcome]}`}>
        {POT_OUTCOME_TEXT[outcome]}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{POT_OUTCOME_BLURB[outcome]}</p>
    </div>
  );
}

/**
 * One answer to a duel.
 *
 * **ITS OWN COMPONENT SO IT CAN TAKE ITS PRESS ON `pointerup`** — `useTapActivate` is a hook and
 * cannot be called inside a `map`. A duellist is frozen and cannot steer, but nothing takes their
 * thumb off the joystick, and a thumb resting there means the platform reports no tap for a
 * second finger and therefore no `click`. That is the bug the player found on the Say button and
 * again on the sayings list; here it would run a ten-second deadline out while somebody pressed a
 * control that could not answer. See `./tap-activate.ts`.
 */
function ChoiceButton({
  choice,
  picked,
  locked,
  note,
  onChoose,
}: {
  choice: MiniGameChoice;
  picked: boolean;
  locked: boolean;
  /** "Sent…" / "In", for a game whose one button has nothing else to say the press was heard. */
  note: string | null;
  onChoose: (choiceId: string) => void;
}) {
  const activate = useTapActivate(() => onChoose(choice.id), locked);
  return (
    <button
      type="button"
      // Locked is locked: the server refuses a second answer, so offering one would be showing
      // the player a control that does nothing.
      disabled={locked}
      {...activate}
      // The id on the element, so a test can say "this game's own answers, in the registry's own
      // order" against the real control rather than against a second drawing of it — which is what
      // the instructions page now reads, since it mounts this card rather than its own tiles.
      data-testid="duel-choice"
      data-choice={choice.id}
      aria-pressed={picked}
      aria-label={choice.label}
      className={`flex min-h-[4.5rem] touch-manipulation select-none flex-col items-center justify-center gap-1.5 rounded-xl border-2 py-3 transition-colors disabled:cursor-not-allowed ${
        picked
          ? 'border-primary bg-primary/15 text-primary'
          : 'border-border bg-background/60 hover:border-primary/60 disabled:opacity-40'
      }`}
    >
      <MiniGameChoiceIcon choice={choice} className="h-8 w-8" />
      <span className="text-[11px] font-semibold uppercase tracking-wide">{choice.label}</span>
      {note === null ? null : (
        <span
          data-testid="duel-choice-note"
          className="text-[10px] font-bold uppercase tracking-widest opacity-80"
        >
          {note}
        </span>
      )}
    </button>
  );
}
